import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaGraduationCap, FaBriefcase, FaCode, FaTrophy, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const Timeline = ({ darkMode }) => {
    const { t } = useTranslation();
    const [expandedItem, setExpandedItem] = useState(null);

    // Default timeline data if translations are not available
    const defaultTimelineData = [
        {
            id: 1,
            date: "2025 - Presente",
            title: "AI Specialist at Hytech",
            description: "Especialista en Inteligencia Artificial desarrollando soluciones empresariales innovadoras. Desarrollo de plataforma de chatbot para CRM con búsquedas en lenguaje natural y sistema de reportes con IA.",
            icon: <FaBriefcase />,
            type: "work",
            details: [
                "Desarrollo de plataforma de chatbot para CRM con búsquedas en base de datos MySQL mediante lenguaje natural",
                "Implementación con LangChain y tools personalizadas para consultas SQL automáticas",
                "Creación de software de reportes con IA para análisis y generación automática de informes",
                "Implementación con Streamlit para interfaces interactivas y FastAPI para servicios backend robustos"
            ]
        },
        {
            id: 2,
            date: "2024 - 2025",
            title: "Web Developer at Click Ping",
            description: "Developed web applications using Django (Python), creating projects like chatbots for Telegram (Aiogram) and WhatsApp (Meta Flow). Database modeling and Django admin panel configuration.",
            icon: <FaBriefcase />,
            type: "work",
            details: [
                "Created RESTful APIs for internal and external services",
                "Implemented authentication and authorization using JWT",
                "Worked with PostgreSQL for database management",
                "Collaborated with UX/UI designers for frontend implementation"
            ]
        },
        {
            id: 3,
            date: "2023",
            title: "International Collegiate Programming Contest (ICPC)",
            description: "Qualified at the regional level, advancing to the Latin American Final 2022, competing with countries such as Cuba, Russia, among others.",
            icon: <FaTrophy />,
            type: "achievement",
            details: [
                "Solved complex algorithmic problems under time pressure",
                "Collaborated with teammates to devise efficient solutions",
                "Practiced advanced data structures and algorithms",
                "Developed problem-solving skills in competitive environments"
            ]
        },
        {
            id: 4,
            date: "2022 - Present",
            title: "Full Stack Development Projects",
            description: "Developed various web applications, including a blog platform, URL shortener, and mobile web applications.",
            icon: <FaCode />,
            type: "project",
            details: [
                "Created a blog web with Java, JDBC, JPA, and Hibernate",
                "Developed a URL shortener service with REST and SOAP APIs",
                "Built mobile-responsive web applications using HTML5 features like LocalStorage and IndexedDB",
                "Implemented secure authentication and data persistence"
            ]
        },
        {
            id: 5,
            date: "2020 - 2024",
            title: "Systems Engineering Graduate - PUCMM",
            description: "Graduado en Ingeniería en Sistemas de la Pontificia Universidad Católica Madre y Maestra, con formación en desarrollo de software, bases de datos, redes y gestión de proyectos tecnológicos.",
            icon: <FaGraduationCap />,
            type: "education",
            details: [
                "Coursework in algorithms, data structures, and design patterns",
                "Database management and systems architecture",
                "Software engineering practices and methodologies",
                "Network infrastructure and security protocols"
            ]
        },
        {
            id: 6,
            date: "2019",
            title: "Secondary Education at Politécnico Ernesto Disla Nuñez",
            description: "Completed secondary education with a focus on science and technology.",
            icon: <FaGraduationCap />,
            type: "education",
            details: [
                "Graduated with honors",
                "Participated in programming and mathematics competitions",
                "Developed foundational understanding of computing principles",
                "Led student technology projects"
            ]
        }
    ];

    // Get timeline data from translations or use default
    let timelineData;
    try {
        const translatedTimeline = t('timeline.items', { returnObjects: true });
        timelineData = Array.isArray(translatedTimeline) ? translatedTimeline : defaultTimelineData;
    } catch (error) {
        console.error("Error retrieving timeline data:", error);
        timelineData = defaultTimelineData;
    }

    // Function to toggle expanded state for a timeline item
    const toggleItemExpansion = (itemId) => {
        if (expandedItem === itemId) {
            setExpandedItem(null);
        } else {
            setExpandedItem(itemId);
        }
    };

    // Get icon component based on item type
    const getIconComponent = (item) => {
        if (item.icon) return item.icon;

        switch (item.type) {
            case 'work':
                return <FaBriefcase />;
            case 'education':
                return <FaGraduationCap />;
            case 'project':
                return <FaCode />;
            case 'achievement':
                return <FaTrophy />;
            default:
                return <FaBriefcase />;
        }
    };

    // Motion variants for animations
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5 }
        }
    };

    return (
        <section id="timeline" className={`py-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 shadow-lg my-16`}>
            <h2 className={`text-3xl font-bold mb-8 text-center ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                {t('timeline.title', 'My Journey')}
            </h2>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="relative"
            >
                {/* Central timeline line */}
                <div className={`absolute left-4 md:left-1/2 ml-1 md:-ml-0.5 w-1 h-full ${darkMode ? 'bg-cyan-600' : 'bg-cyan-500'} rounded-full`}></div>

                {/* Timeline items */}
                {timelineData.map((item, index) => (
                    <motion.div
                        key={item.id}
                        variants={itemVariants}
                        className={`flex md:even:flex-row-reverse mb-8 md:mb-16 relative`}
                    >
                        {/* Timeline dot with icon */}
                        <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 mt-5">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 ${
                                darkMode ? 'bg-gray-700 text-cyan-400 border-cyan-600' : 'bg-white text-cyan-600 border-cyan-500'
                            } border-2`}>
                                {getIconComponent(item)}
                            </div>
                        </div>

                        {/* Content card */}
                        <div className={`md:w-5/12 ml-16 md:ml-0 ${
                            index % 2 === 0 ? 'md:mr-auto md:text-right' : 'md:ml-auto'
                        }`}>
                            <div className={`p-5 rounded-lg shadow-md transition-all duration-300 ${
                                expandedItem === item.id
                                    ? (darkMode ? 'bg-gray-700 border-cyan-400' : 'bg-gray-50 border-cyan-500')
                                    : (darkMode ? 'bg-gray-700 hover:border-cyan-400' : 'bg-gray-50 hover:border-cyan-500')
                            } border-l-4`}>
                                {/* Date pill */}
                                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-2 ${
                                    darkMode ? 'bg-gray-600 text-cyan-300' : 'bg-gray-200 text-cyan-700'
                                }`}>
                                    {item.date}
                                </span>

                                <h3 className="text-xl font-bold mb-2">{item.title}</h3>

                                <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {item.description}
                                </p>

                                {/* Display details if they exist and the item is expanded */}
                                {item.details && (
                                    <div className="mt-2">
                                        <button
                                            onClick={() => toggleItemExpansion(item.id)}
                                            className={`flex items-center gap-1 text-sm font-medium ${
                                                darkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'
                                            }`}
                                        >
                                            {expandedItem === item.id ? (
                                                <>
                                                    {t('timeline.viewLess', 'View less')} <FaChevronUp className="text-xs" />
                                                </>
                                            ) : (
                                                <>
                                                    {t('timeline.viewMore', 'View details')} <FaChevronDown className="text-xs" />
                                                </>
                                            )}
                                        </button>

                                        {expandedItem === item.id && (
                                            <motion.ul
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className={`mt-3 space-y-1 text-sm ${
                                                    darkMode ? 'text-gray-300' : 'text-gray-600'
                                                } ${index % 2 === 0 ? 'md:text-right' : ''}`}
                                            >
                                                {item.details.map((detail, detailIndex) => (
                                                    <li key={detailIndex} className="py-1">
                                                        • {detail}
                                                    </li>
                                                ))}
                                            </motion.ul>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </motion.div>
        </section>
    );
};

export default Timeline;